import { Conference } from "@easycv/common/types/conference";
import {
  ContextConstants,
  ContextMethods,
  ContextState
} from "@easycv/common/types/context";
import { Backend } from "@easycv/common/types/util";
import { CCSetup } from "./CCSetup";
import { CCStyling } from "./CCStyling";
import { CCMultiActive, CCMultiSetup } from "./types/multi";

const multi_cc_root = "__multi_cc_root";
const multi_cc_tab_row = "__multi_cc_tab_row";
const multi_cc_tab_row_styling = CCStyling.fromObject({
  display: "flex",
  width: "100%",
  "border-bottom": "2px solid #e4e2e2",
  "padding-top": "10px"
});
const multi_cc_tab_item = "__multi_cc_tab_item";
const multi_cc_tab_item_styling = (selected: boolean) =>
  CCStyling.fromObject({
    "background-color": selected ? "#337ab7" : "#e4e2e2",
    color: selected ? "white" : "inherit",
    padding: "6px 16px",
    "user-select": "none",
    cursor: "pointer",
    "margin-right": "6px",
    "border-radius": "6px 6px 0px 0px",
    "border-bottom": "none"
  }).compile();

const tabRowElement = (selected: boolean, id: number, title: string) => {
  const attrs = [
    `class="${multi_cc_tab_item}"`,
    `data-id="${id}"`,
    `style="${multi_cc_tab_item_styling(selected)}"`
  ].join(" ");
  return `<div ${attrs}>${title}</div>`;
};

export class CCMultiRule {
  private visible: () => boolean;
  private accessible: () => boolean;
  private editable: () => boolean;
  constructor() {
    const __default = () => true;
    this.visible = __default;
    this.accessible = __default;
    this.editable = __default;
  }
  public visibleIf(fn: typeof this.visible) {
    this.visible = fn;
  }
  public accessibleIf(fn: typeof this.accessible) {
    this.accessible = fn;
  }
  public editableIf(fn: typeof this.editable) {
    this.editable = fn;
  }
  public run(): [boolean, boolean, boolean] {
    return [this.visible(), this.accessible(), this.editable()];
  }
}
type PersistentFunction = (cc: CCSetup, active: CCMultiActive) => void;
export class CCMulti {
  private setups: CCMultiSetup[];
  private conference: Conference;
  private backend: Backend;
  private contextMethods: ContextMethods;
  private contextConstants: ContextConstants;
  private contextState: ContextState;
  private using?: CCSetup;
  private activeSetup?: CCMultiSetup;
  private persistentFunctions: PersistentFunction[];
  constructor(
    conference: Conference,
    backend: Backend,
    contextMethods: ContextMethods,
    contextConstants: ContextConstants,
    contextState: ContextState
  ) {
    this.conference = conference;
    this.backend = backend;
    this.contextMethods = contextMethods;
    this.contextConstants = contextConstants;
    this.contextState = contextState;
    this.setups = [];
    this.using = undefined;
    this.persistentFunctions = [];
    $("#fieldList").empty();
    $("#fieldList").append(
      `<div id="${multi_cc_root}" class="cc-root-class"><div id="${multi_cc_tab_row}" style="${multi_cc_tab_row_styling.compile()}"></div></div>`
    );
  }
  private resolveIdentifierToIndex(identifier: number | string) {
    return typeof identifier === "number"
      ? identifier
      : this.setups?.findIndex(
          ({ dataPrefix, title, shortTitle }) =>
            dataPrefix === identifier ||
            title === identifier ||
            (shortTitle === identifier && identifier !== undefined)
        );
  }
  private refresh(callPersistentFunctions: boolean = true) {
    // where to put user if they are currently in a invisible/unaccessible tab when the refresh is done
    let landingPoint = undefined;
    let current = undefined;
    let doNotLand = [];
    for (let i = 0; i < this.setups.length; ++i) {
      const [visible, accessible, editable] = this.setups[i].rule.run();
      const element = $(`.${multi_cc_tab_item}[data-id="${i}"]`);
      if (this.using?.dataPrefix === this.setups[i].dataPrefix) {
        current = i;
      }
      if (visible) {
        if (element.length) {
          element.replaceWith(
            tabRowElement(
              this.using?.dataPrefix == this.setups[i].dataPrefix,
              i,
              this.setups[i].title
            )
          );
        } else {
          $(`#${multi_cc_tab_row}`).append(
            tabRowElement(
              this.using?.dataPrefix == this.setups[i].dataPrefix,
              i,
              this.setups[i].title
            )
          );
        }
      } else {
        element.remove();
      }
      if (visible && accessible) {
        $(`.${multi_cc_tab_item}[data-id="${i}"]`).on("click", () => {
          this.use(i);
        });
        landingPoint = i;
      } else {
        $(`.${multi_cc_tab_item}[data-id="${i}"]`).off("click");
        doNotLand.push(i);
      }
    }
    if (landingPoint === undefined) {
      // user is in some bad state, refresh
      window.location.reload();
    } else if (doNotLand.includes(current)) {
      // dont click where we already are
      // not checking this == infinite refresh loop
      if (current !== landingPoint) {
        $(`.${multi_cc_tab_item}[data-id="${landingPoint}"]`).trigger("click");
      }
    }
    if (callPersistentFunctions) {
      for (const fn of this.persistentFunctions) {
        fn(this.using, {
          title: this.activeSetup.title,
          dataPrefix: this.activeSetup.dataPrefix,
          shortTitle: this.activeSetup.shortTitle
        });
      }
    }
  }
  public add({
    title,
    dataPrefix,
    onActivate,
    updateCallback = () => {},
    shortTitle = undefined,
    statusChangeRules
  }: Omit<CCMultiSetup, "init" | "rule">) {
    const id = this.setups.length;
    $(`#${multi_cc_tab_row}`).append(tabRowElement(false, id, title));
    this.setups.push({
      title,
      shortTitle,
      dataPrefix,
      onActivate,
      rule: new CCMultiRule(),
      init: () => {
        const cc = new CCSetup(
          this.conference,
          this.backend,
          dataPrefix,
          this.contextMethods,
          this.contextConstants,
          this.contextState,
          updateCallback,
          {
            ccRoot: `#${multi_cc_root}`,
            independent: false,
            notifyWhenUpdating: () => {
              this.refresh(false);
            },
            statusChangeChecks: {
              submitForReview: () => statusChangeRules?.allowSubmit ?? true,
              retractFromReview: () => statusChangeRules?.allowUnsubmit ?? true,
              finalize: () => statusChangeRules?.allowFinalize ?? true,
              retractFinalize: () => statusChangeRules?.allowUnfinalize ?? true
            }
          }
        );
        return cc;
      }
    });
  }
  public use(identifier: number | string) {
    $(`#${multi_cc_root}`)
      .find("*")
      .not(`#${multi_cc_tab_row},.${multi_cc_tab_item}`)
      .remove();
    const index = this.resolveIdentifierToIndex(identifier);
    this.using = this.setups?.[index]?.init?.();
    this.activeSetup = this.setups?.[index];
    if (this.using != undefined) this.setups[index].onActivate(this.using);
    this.refresh();
  }
  public currentReference(): CCSetup | undefined {
    return this.using;
  }
  public persist(fn: PersistentFunction) {
    this.persistentFunctions.push(fn);
  }
  public rules(identifier: number | string) {
    const index = this.resolveIdentifierToIndex(identifier);
    return this.setups[index].rule;
  }
}
